/**
 * Canje de vales de un crédito (liquidar los vales pendientes).
 *
 * Puerto de `Credito::settlement` + `views/menu/credito/settlement.php` y de
 * `API/credit/settlement`, la pantalla que el listado de créditos del panel
 * anterior abría con el botón **Canjear** de cada fila.
 *
 * Qué se porta:
 * - El **listado de vales pendientes por liquidar** (`credito_venta` activos,
 *   sin liquidar y con importe) con los filtros de fechas y de placa del
 *   original, la selección de vales y los totales de cantidad e importe
 *   seleccionados frente a los disponibles.
 * - El **enlace con una factura existente** (`/credito/enlazar_factura`): marca
 *   los vales elegidos como liquidados con un comprobante ya emitido.
 * - El **pre-cálculo** («Pre Procesar») de los comprobantes que se emitirían
 *   según el tipo de liquidación: por despacho, por producto, por total, por
 *   placa, por transacción o por producto y precio.
 * - Los selectores del original de tipo de comprobante y serie
 *   (`document_series` administrativas) y las **condiciones de pago**
 *   (`API/Invoicer::settlement_post`), que son las que hacen que el documento
 *   salga bien:
 *   - **CRÉDITO**: con su **plazo** (7/15/20/30/40/45 días); el vencimiento del
 *     comprobante es la fecha de emisión más el plazo.
 *   - **CONTADO**: con su **destino del pago**; caja chica pide el concepto o
 *     referencia y la transferencia bancaria pide cuenta destino y **referencia
 *     bancaria obligatoria** (el comprobante adjunto se sube en el panel
 *     anterior).
 *   - **Fecha de emisión** limitada a los últimos `dias_boleta_masiva` días.
 *   - A un cliente con **DNI** sólo se le puede emitir **boleta**.
 *
 * - La **emisión de los comprobantes** («Liquidar»): crea las ventas
 *   administrativas con su detalle y su comprobante (`estado_sunat = 'En
 *   Validación'`, la cola que consume la ticketera) y marca los vales como
 *   liquidados, con el correlativo de la serie y el reparto por tipo de
 *   liquidación del original, pero **en EDSuite API** (sin pasar por el panel
 *   anterior).
 *
 * - Los **reemplazos** del original antes de facturar: la **placa única**
 *   (`override_plate`), el **texto de los vales** (`override_additional`) y las
 *   **placas por producto** del pre-cálculo.
 */

import {
  CalendarDays,
  HandCoins,
  Info,
  Landmark,
  PencilLine,
  Percent,
  Receipt,
} from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import {
  useEnlazarVales,
  useLiquidarCanje,
  useParametrosCanje,
  usePrecalculoCanje,
  useSeriesCanje,
  useValesCanje,
} from '../cierre.api';
import { TIPOS_LIQUIDACION, type FiltrosValesCanje } from '../cierre-gestion.types';
import { fechaCorta, hoyIso, monto } from '../gestion-formato';

const INICIO_POR_DEFECTO = '2022-09-02';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface Props {
  /** Crédito abierto (`null` cierra el diálogo). */
  id: number | null;
  onClose: () => void;
  /** Aviso de éxito que muestra la pantalla. */
  onAviso: (mensaje: string) => void;
}

export function CreditoCanjearDialog({ id, onClose, onAviso }: Props) {
  const [filtros, setFiltros] = useState<FiltrosValesCanje>(() => ({
    desde: INICIO_POR_DEFECTO,
    hasta: hoyIso(),
  }));
  const [placa, setPlaca] = useState('');
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [tipo, setTipo] = useState(3);
  const [comprobante, setComprobante] = useState('FACTURA');
  const [serie, setSerie] = useState('');
  const [metodoPago, setMetodoPago] = useState('CREDITO');
  const [plazo, setPlazo] = useState(7);
  const [emision, setEmision] = useState(() => hoyIso());
  const [destino, setDestino] = useState('caja_chica');
  const [referencia, setReferencia] = useState('');
  const [cuentaId, setCuentaId] = useState(0);
  const [referenciaBancaria, setReferenciaBancaria] = useState('');
  const [error, setError] = useState<string | null>(null);

  // La placa filtra igual que las fechas (el original la aplicaba con «Consultar»).
  const consulta = useValesCanje(id, { ...filtros, placa });
  const series = useSeriesCanje(comprobante);
  const parametros = useParametrosCanje();
  const precalculo = usePrecalculoCanje();
  const enlazar = useEnlazarVales(id);
  const liquidar = useLiquidarCanje(id);
  const [documento, setDocumento] = useState('');
  const [avisoEnlace, setAvisoEnlace] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const [emitidos, setEmitidos] = useState<string[]>([]);
  const [placaUnica, setPlacaUnica] = useState('');
  const [reemplazarPlaca, setReemplazarPlaca] = useState(false);
  const [textoVales, setTextoVales] = useState('');
  const [reemplazarTexto, setReemplazarTexto] = useState(false);
  const [placasEditadas, setPlacasEditadas] = useState<Record<number, string>>({});

  const cuentas = parametros.data?.cuentas ?? [];
  const diasBoletaMasiva = parametros.data?.dias_boleta_masiva ?? 3;
  const seriePorDefecto =
    comprobante === 'FACTURA'
      ? parametros.data?.series_por_defecto.FACTURA
      : parametros.data?.series_por_defecto.BOLETA;

  /** Primer día con el que se puede emitir una boleta masiva. */
  const emisionMinima = (() => {
    const fecha = new Date(`${hoyIso()}T00:00:00Z`);
    fecha.setUTCDate(fecha.getUTCDate() - diasBoletaMasiva);

    return fecha.toISOString().slice(0, 10);
  })();

  // A un cliente con DNI sólo se le puede emitir boleta (`settlement.php`).
  const tipoDoc = String(consulta.data?.credito.tipo_doc ?? '').trim().toUpperCase();
  const esDni = tipoDoc === '1' || tipoDoc === 'DNI';

  if (esDni && comprobante === 'FACTURA') {
    setComprobante('BOLETA');
    setSerie('');
  }

  // El original deja elegida la primera serie del catálogo (o la de la empresa).
  const seriesDisponibles = series.data?.series ?? [];
  const serieElegida = serie || seriePorDefecto || seriesDisponibles[0] || '';

  if (serieElegida && serieElegida !== serie) {
    setSerie(serieElegida);
  }

  const vales = consulta.data?.vales ?? [];
  const totales = consulta.data?.totales;
  const sinProducto = consulta.data?.sin_producto;

  // Al llegar (o cambiar) el listado se seleccionan todos los vales, como el
  // original (que los marca todos y permite liberarlos con Alt + N).
  const firma = `${id ?? 0}|${filtros.desde ?? ''}|${filtros.hasta ?? ''}|${placa}|${vales.map((vale) => vale.id).join(',')}`;
  const [firmaVista, setFirmaVista] = useState('');

  if (firma !== firmaVista) {
    setFirmaVista(firma);
    setSeleccion(new Set(vales.map((vale) => vale.id)));
    setError(null);
    // Las placas editadas parten de las de los vales.
    setPlacasEditadas({});
  }

  // Sin `useMemo`: el listado se recalcula en cada render y es barato.
  const escogidos = vales.filter((vale) => seleccion.has(vale.id));

  const seleccionados = {
    vales: escogidos.length,
    cantidad: escogidos.reduce((suma, vale) => suma + vale.cantidad, 0),
    importe: escogidos.reduce((suma, vale) => suma + vale.total, 0),
  };

  const alternar = (valeId: number, marcado: boolean) =>
    setSeleccion((previo) => {
      const copia = new Set(previo);

      if (marcado) copia.add(valeId);
      else copia.delete(valeId);

      return copia;
    });

  const todos = vales.length > 0 && seleccion.size === vales.length;

  /** Condiciones con las que se pre-calcula y se emite (las de crédito/contado). */
  const condiciones = {
    tipo,
    vales: [...seleccion],
    payment: metodoPago,
    // El plazo sólo cuenta si el documento sale a crédito.
    credit_term: metodoPago === 'CREDITO' ? plazo : undefined,
    issue_date: emision,
    voucher_type: comprobante,
    voucher_serie: serieElegida || undefined,
    contado_destino: metodoPago === 'CONTADO' ? destino : undefined,
    contado_referencia: referencia.trim() || undefined,
    contado_banco_id: metodoPago === 'CONTADO' && destino === 'transferencia' ? cuentaId : undefined,
    contado_referencia_bancaria: referenciaBancaria.trim() || undefined,
    // Reemplazos antes de facturar (`override_plate` / `override_additional`).
    override_plate: reemplazarPlaca,
    plate: reemplazarPlaca ? placaUnica.trim() : undefined,
    override_additional: reemplazarTexto,
    additional: reemplazarTexto ? textoVales.trim() : undefined,
    edited_plates: Object.keys(placasEditadas).length
      ? Object.fromEntries(
          Object.entries(placasEditadas)
            .map(([producto, placas]) => [
              producto,
              placas
                .split(',')
                .map((placa) => placa.trim())
                .filter(Boolean),
            ])
            .filter(([, placas]) => (placas as string[]).length > 0),
        )
      : undefined,
  };

  const calcular = () => {
    if (id === null) return;

    setError(null);
    precalculo.mutate(
      {
        id,
        datos: condiciones,
      },
      {
        onError: (fallo) =>
          setError(
            fallo instanceof ApiError ? fallo.message : 'No se pudo preparar la liquidación.',
          ),
      },
    );
  };

  const cambioDeListado = (cambios: Partial<FiltrosValesCanje>) =>
    setFiltros((previo) => ({ ...previo, ...cambios }));

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="xl"
      title={
        consulta.data
          ? `Canje de vales · ${consulta.data.credito.cliente}`
          : 'Canje de vales (liquidar en un comprobante)'
      }
      description={
        consulta.data
          ? `${consulta.data.credito.numero_doc} · ${consulta.data.credito.producto} · línea ${monto(consulta.data.credito.linea)}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="outline"
            size="sm"
            loading={precalculo.isPending}
            disabled={!seleccion.size}
            onClick={calcular}
          >
            Pre procesar
          </Button>
          <Button
            size="sm"
            loading={liquidar.isPending}
            disabled={!seleccion.size}
            title="Emitir los comprobantes de la liquidación"
            leftIcon={<Receipt className="size-4" />}
            onClick={() => {
              setError(null);
              setConfirmar(true);
            }}
          >
            Liquidar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <Input
            label="Desde"
            type="date"
            value={filtros.desde ?? ''}
            onChange={(evento) => cambioDeListado({ desde: evento.target.value })}
          />
          <Input
            label="Hasta"
            type="date"
            value={filtros.hasta ?? ''}
            onChange={(evento) => cambioDeListado({ hasta: evento.target.value })}
          />
          <Input
            label="Filtrar por placa"
            placeholder="Ej: ABC-123"
            value={placa}
            onChange={(evento) => setPlaca(evento.target.value)}
          />
          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={!id || consulta.isFetching}
              onClick={() => void consulta.refetch()}
            >
              Consultar
            </Button>
          </div>
        </div>

        {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

        {consulta.isError ? (
          <Alert variant="error" title="No se pudieron leer los vales">
            {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        ) : null}

        {consulta.data ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-500">
                Vales pendientes por liquidar: {totales?.vales ?? 0} · {monto(totales?.cantidad)} GLL
                · S/ {monto(totales?.importe)}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!vales.length}
                  onClick={() => setSeleccion(new Set(vales.map((vale) => vale.id)))}
                >
                  Seleccionar todos
                </Button>
                <Button variant="ghost" size="sm" disabled={!seleccion.size} onClick={() => setSeleccion(new Set())}>
                  Liberar
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3 rounded-card border border-ink-100 bg-ink-50/40 p-3">
              <label className="flex min-w-64 flex-1 flex-col gap-1.5 text-sm">
                <span className="font-medium text-ink-700">Enlazar con factura existente</span>
                <input
                  className={CONTROL}
                  placeholder="Comprobante ya emitido, ej: FE05-12"
                  value={documento}
                  onChange={(evento) => setDocumento(evento.target.value)}
                />
              </label>
              <Button
                variant="outline"
                size="sm"
                loading={enlazar.isPending}
                disabled={!documento.trim() || !seleccion.size}
                onClick={() => {
                  setAvisoEnlace(null);
                  enlazar.mutate(
                    { vales: [...seleccion], documento: documento.trim() },
                    {
                      onSuccess: (respuesta) => {
                        setAvisoEnlace(respuesta.mensaje);
                        setDocumento('');
                      },
                      onError: (fallo) =>
                        setAvisoEnlace(
                          fallo instanceof ApiError ? fallo.message : 'No se pudieron enlazar los vales.',
                        ),
                    },
                  );
                }}
              >
                Enlazar
              </Button>
              {avisoEnlace ? (
                <p className="text-xs text-ink-600">{avisoEnlace}</p>
              ) : null}
            </div>

            <div className="max-h-[38vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-3 py-2">
                      <input
                        type="checkbox"
                        className="size-4 accent-brand-500"
                        aria-label="Seleccionar todos los vales"
                        checked={todos}
                        onChange={(evento) =>
                          setSeleccion(
                            evento.target.checked ? new Set(vales.map((vale) => vale.id)) : new Set(),
                          )
                        }
                      />
                    </th>
                    <th className="px-3 py-2 whitespace-nowrap">Fecha</th>
                    <th className="px-3 py-2 whitespace-nowrap">Número</th>
                    <th className="px-3 py-2 whitespace-nowrap">Producto</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Cantidad × precio</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                    <th className="px-3 py-2 whitespace-nowrap">Personal</th>
                  </tr>
                </thead>
                <tbody>
                  {vales.map((vale) => (
                    <tr
                      key={vale.id}
                      className={
                        seleccion.has(vale.id)
                          ? 'border-t border-ink-100 bg-brand-50/40'
                          : 'border-t border-ink-100'
                      }
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          className="size-4 accent-brand-500"
                          aria-label={`Liquidar el vale ${vale.numero}`}
                          checked={seleccion.has(vale.id)}
                          onChange={(evento) => alternar(vale.id, evento.target.checked)}
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(vale.fecha)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{vale.numero}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{vale.producto}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {monto(vale.cantidad)} × {monto(vale.precio)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap text-ink-800">
                        {monto(vale.total)}
                      </td>
                      <td className="px-3 py-2">
                        <span className="block truncate">{vale.personal ?? '—'}</span>
                        {vale.placa || vale.vale ? (
                          <span className="block truncate text-xs text-ink-500">
                            {[vale.placa, vale.vale ? `vale ${vale.vale}` : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}

                  {vales.length === 0 ? (
                    <tr>
                      <td className="px-3 py-6 text-center text-ink-500" colSpan={7}>
                        No hay vales pendientes de liquidar en el rango.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
                <tfoot className="bg-ink-50/60">
                  <tr className="border-t border-ink-100 font-semibold text-ink-800">
                    <td className="px-3 py-2" colSpan={4}>
                      Seleccionado / disponible
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {monto(seleccionados.cantidad)} / {monto(totales?.cantidad)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {monto(seleccionados.importe)} / {monto(totales?.importe)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {seleccionados.vales} de {totales?.vales ?? 0} vales
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {sinProducto && sinProducto.vales > 0 ? (
              <p className="text-xs text-ink-500">
                {sinProducto.vales} vale(s) por S/ {monto(sinProducto.importe)} no tienen producto
                asignado: el panel anterior tampoco los lista, así que no entran en la liquidación
                (por eso el «por canjear» de la lista puede ser mayor).
              </p>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
              <section className="rounded-card border border-ink-100 bg-white p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <HandCoins className="size-4 text-brand-500" aria-hidden="true" />
                  Liquidación
                </h3>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Tipo de liquidación</span>
                    <Select
                      className={CONTROL}
                      value={tipo}
                      onChange={(evento) => setTipo(Number(evento.target.value))}
                    >
                      {TIPOS_LIQUIDACION.map((item) => (
                        <option key={item.valor} value={item.valor}>
                          {item.etiqueta}
                        </option>
                      ))}
                    </Select>
                  </label>

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Tipo de comprobante</span>
                    <Select
                      className={CONTROL}
                      value={comprobante}
                      onChange={(evento) => {
                        setComprobante(evento.target.value);
                        setSerie('');
                      }}
                    >
                      <option value="FACTURA" disabled={esDni}>
                        FACTURA ELECTRÓNICA
                      </option>
                      <option value="BOLETA">BOLETA DE VENTA ELECTRÓNICA</option>
                    </Select>
                  </label>

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Fecha de emisión</span>
                    <CampoFecha
                      className={CONTROL}
                     
                      min={emisionMinima}
                      max={hoyIso()}
                      value={emision}
                      onChange={(evento) => setEmision(evento.target.value)}
                     tipo="date" />
                  </label>

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Serie</span>
                    <Select
                      className={CONTROL}
                      value={serieElegida}
                      onChange={(evento) => setSerie(evento.target.value)}
                    >
                      <option value="">Por asignar</option>
                      {(series.data?.series ?? []).map((codigo) => (
                        <option key={codigo} value={codigo}>
                          {codigo}
                        </option>
                      ))}
                    </Select>
                  </label>

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Método de pago</span>
                    <Select
                      className={CONTROL}
                      value={metodoPago}
                      onChange={(evento) => setMetodoPago(evento.target.value)}
                    >
                      {(
                        parametros.data?.metodos_pago ?? [
                          { valor: 'CREDITO', etiqueta: 'CRÉDITO' },
                          { valor: 'CONTADO', etiqueta: 'CONTADO' },
                        ]
                      ).map((item) => (
                        <option key={item.valor} value={item.valor}>
                          {item.etiqueta}
                        </option>
                      ))}
                    </Select>
                  </label>

                  {metodoPago === 'CREDITO' ? (
                    <label className="flex flex-col gap-1.5 text-sm">
                      <span className="font-medium text-ink-700">Plazo de crédito</span>
                      <Select
                        className={CONTROL}
                        value={plazo}
                        onChange={(evento) => setPlazo(Number(evento.target.value))}
                      >
                        {(parametros.data?.plazos ?? [7, 15, 20, 30, 40, 45]).map((dias) => (
                          <option key={dias} value={dias}>
                            {dias} días
                          </option>
                        ))}
                      </Select>
                    </label>
                  ) : (
                    <label className="flex flex-col gap-1.5 text-sm">
                      <span className="font-medium text-ink-700">Destino del pago</span>
                      <Select
                        className={CONTROL}
                        value={destino}
                        onChange={(evento) => setDestino(evento.target.value)}
                      >
                        {(parametros.data?.destinos ?? [
                          { valor: 'caja_chica', etiqueta: 'Caja Chica' },
                          { valor: 'transferencia', etiqueta: 'Transferencia bancaria' },
                        ]).map((item) => (
                          <option key={item.valor} value={item.valor}>
                            {item.etiqueta}
                          </option>
                        ))}
                      </Select>
                    </label>
                  )}

                  {metodoPago === 'CONTADO' && destino === 'caja_chica' ? (
                    <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
                      <span className="font-medium text-ink-700">Concepto / Referencia</span>
                      <input
                        className={CONTROL}
                        placeholder="Ej: Liquidación de vales"
                        value={referencia}
                        onChange={(evento) => setReferencia(evento.target.value)}
                      />
                    </label>
                  ) : null}

                  {metodoPago === 'CONTADO' && destino === 'transferencia' ? (
                    <>
                      <label className="flex flex-col gap-1.5 text-sm">
                        <span className="font-medium text-ink-700">Cuenta destino</span>
                        <Select
                          className={CONTROL}
                          value={cuentaId}
                          onChange={(evento) => setCuentaId(Number(evento.target.value))}
                        >
                          <option value={0}>SELECCIONE</option>
                          {cuentas.map((cuenta) => (
                            <option key={cuenta.id} value={cuenta.id}>
                              {[cuenta.descripcion, cuenta.numero].filter(Boolean).join(' - ')}
                            </option>
                          ))}
                        </Select>
                      </label>

                      <label className="flex flex-col gap-1.5 text-sm">
                        <span className="font-medium text-ink-700">
                          Referencia bancaria <span className="text-danger-500">*</span>
                        </span>
                        <input
                          className={CONTROL}
                          placeholder="Nro. operación"
                          value={referenciaBancaria}
                          onChange={(evento) => setReferenciaBancaria(evento.target.value)}
                        />
                      </label>
                    </>
                  ) : null}
                </div>

                <p className="mt-3 text-xs text-ink-500">
                  Botón <b>Pre procesar</b>: muestra los comprobantes que se emitirían con los vales
                  seleccionados y el tipo de liquidación elegido.
                </p>

                {metodoPago === 'CREDITO' ? (
                  <p className="mt-1 text-xs text-ink-500">
                    El comprobante sale <b>a crédito</b> con {plazo} días de plazo.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-ink-500">
                    El comprobante sale <b>al contado</b>
                    {destino === 'transferencia'
                      ? ' y se registra el ingreso en la cuenta elegida con su referencia bancaria.'
                      : ' y se registra el ingreso en caja chica con el concepto indicado.'}
                  </p>
                )}

                {esDni ? (
                  <Alert variant="warning" title="Cliente con DNI">
                    <span className={ICONO_EN_ALERTA}>
                      <Info className="size-4" aria-hidden="true" />
                      A un cliente con DNI sólo se le puede emitir <b>boleta</b>: la factura está
                      deshabilitada.
                    </span>
                  </Alert>
                ) : null}
              </section>

              <section className="rounded-card border border-ink-100 bg-ink-50/40 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <Receipt className="size-4 text-brand-500" aria-hidden="true" />
                  Comprobantes a emitir
                </h3>

                {error ? (
                  <Alert variant="error" title="No se pudo preparar la liquidación">
                    {error}
                  </Alert>
                ) : null}

                {precalculo.isPending ? <Skeleton className="h-40 w-full" /> : null}

                {precalculo.data ? (
                  <div className="space-y-3">
                    <p className="text-xs text-ink-500">
                      <Badge variant="brand">{precalculo.data.tipo_descripcion}</Badge>{' '}
                      {precalculo.data.total_comprobantes} comprobante(s) por S/{' '}
                      {monto(precalculo.data.total_importe)}
                    </p>

                    <dl className="space-y-1 rounded-control border border-ink-100 bg-white px-3 py-2 text-xs">
                      <div className="flex items-center justify-between gap-3">
                        <dt className="flex items-center gap-1.5 text-ink-500">
                          <CalendarDays className="size-3.5" aria-hidden="true" />
                          Condición
                        </dt>
                        <dd className="font-semibold text-ink-800">
                          {precalculo.data.condicion.payment === 'CREDITO'
                            ? `CRÉDITO ${precalculo.data.condicion.plazo} días`
                            : `CONTADO · ${precalculo.data.condicion.destino_pago === 'transferencia' ? 'Transferencia' : 'Caja Chica'}`}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="text-ink-500">
                          {precalculo.data.condicion.payment === 'CREDITO'
                            ? 'Emisión / vencimiento'
                            : 'Fecha de emisión'}
                        </dt>
                        <dd className="font-semibold text-ink-800">
                          {fechaCorta(precalculo.data.condicion.fecha_emision)}
                          {precalculo.data.condicion.payment === 'CREDITO' &&
                          precalculo.data.condicion.fecha_vencimiento
                            ? ` · ${fechaCorta(precalculo.data.condicion.fecha_vencimiento)}`
                            : ''}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <dt className="flex items-center gap-1.5 text-ink-500">
                          <Landmark className="size-3.5" aria-hidden="true" />
                          {precalculo.data.condicion.payment === 'CREDITO' ? 'Serie' : 'Pago'}
                        </dt>
                        <dd className="font-semibold text-ink-800">
                          {precalculo.data.condicion.payment === 'CREDITO'
                            ? (precalculo.data.condicion.voucher_serie ?? 'Por asignar')
                            : (precalculo.data.condicion.referencia_bancaria ??
                              precalculo.data.condicion.referencia ??
                              '—')}
                        </dd>
                      </div>
                    </dl>
                    <ul className="divide-y divide-ink-100 overflow-hidden rounded-control border border-ink-100 bg-white">
                      {precalculo.data.comprobantes.map((item) => (
                        <li
                          key={item.etiqueta}
                          className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-ink-800">
                              {item.etiqueta}
                            </span>
                            <span className="block truncate text-xs text-ink-500">
                              {item.detalle} · {item.vales} vale(s)
                            </span>
                          </span>
                          <span className="text-right whitespace-nowrap">
                            <span className="block font-semibold text-ink-800">
                              S/ {monto(item.importe)}
                            </span>
                            <span className="block text-xs text-ink-500">
                              {monto(item.cantidad)} GLL
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-ink-500">
                    Pulsa <b>Pre procesar</b> para ver los comprobantes que saldrían de la
                    liquidación.
                  </p>
                )}
              </section>
            </div>

            {emitidos.length ? (
              <Alert variant="success" title="Comprobantes emitidos">
                <span className={ICONO_EN_ALERTA}>
                  <Receipt className="size-4" aria-hidden="true" />
                  {emitidos.join(', ')}. Quedan en la cola de SUNAT («En Validación») y los vales ya
                  figuran liquidados con ese comprobante.
                </span>
              </Alert>
            ) : null}

            <section className="rounded-card border border-ink-100 bg-white p-4">
              <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink-800">
                <PencilLine className="size-4 text-brand-500" aria-hidden="true" />
                Reemplazos antes de facturar
              </h3>
              <p className="mb-3 text-xs text-ink-500">
                Sobrescriben los datos originales de los vales en el comprobante, como el original.
              </p>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Checkbox
                    label="¿Reemplazar todas las PLACAS por una?"
                    checked={reemplazarPlaca}
                    disabled={tipo !== 1}
                    onChange={(evento) => {
                      setReemplazarPlaca(evento.target.checked);

                      if (!evento.target.checked) setPlacaUnica('');
                    }}
                  />
                  <input
                    className={CONTROL}
                    placeholder="Placa, ej: ABC-123"
                    disabled={!reemplazarPlaca || tipo !== 1}
                    value={placaUnica}
                    onChange={(evento) => setPlacaUnica(evento.target.value)}
                  />
                  {tipo !== 1 ? (
                    <p className="text-xs text-ink-500">
                      Sólo aplica a «Factura por Despacho»; en el resto las placas se editan por
                      producto.
                    </p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Checkbox
                    label="¿Reemplazar los VALES por un texto?"
                    checked={reemplazarTexto}
                    onChange={(evento) => {
                      setReemplazarTexto(evento.target.checked);

                      if (!evento.target.checked) setTextoVales('');
                    }}
                  />
                  <input
                    className={CONTROL}
                    placeholder="Texto para las observaciones"
                    disabled={!reemplazarTexto}
                    value={textoVales}
                    onChange={(evento) => setTextoVales(evento.target.value)}
                  />
                </div>
              </div>

              {tipo !== 1 && (precalculo.data?.placas_por_producto ?? []).length ? (
                <div className="mt-3 border-t border-ink-100 pt-3">
                  <p className="mb-2 text-xs font-medium text-ink-600">
                    Placas por producto (separadas por comas)
                  </p>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {(precalculo.data?.placas_por_producto ?? []).map((item) => (
                      <li key={item.producto_id} className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm text-ink-700">
                          {item.producto}
                        </span>
                        <input
                          className={cn(CONTROL, 'w-44')}
                          placeholder={item.placas.join(', ') || 'Sin placa'}
                          value={placasEditadas[item.producto_id] ?? ''}
                          onChange={(evento) =>
                            setPlacasEditadas((previo) => ({
                              ...previo,
                              [item.producto_id]: evento.target.value,
                            }))
                          }
                        />
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-ink-500">
                    Si se deja vacío se usan las placas originales del producto.
                  </p>
                </div>
              ) : null}
            </section>

            <Alert variant="info" title="Cómo se agrupan los vales">
              <span className={ICONO_EN_ALERTA}>
                <Info className="size-4" aria-hidden="true" />
                <Percent className="size-4" aria-hidden="true" />
                Cada tipo de liquidación agrupa distinto: por despacho y por transacción sale un
                comprobante por vale; por producto, por placa y por producto y precio agrupan los
                vales que comparten ese dato; y «por total» emite un único comprobante con todo lo
                seleccionado.
              </span>
            </Alert>
          </>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmar}
        title="Emitir los comprobantes"
        description={`Se emitirán ${
          precalculo.data ? `${precalculo.data.total_comprobantes} comprobante(s)` : 'los comprobantes'
        }${
          precalculo.data ? ` por S/ ${monto(precalculo.data.total_importe)}` : ''
        } con ${
          metodoPago === 'CREDITO'
            ? `crédito a ${plazo} días`
            : `pago al contado (${destino === 'transferencia' ? 'transferencia' : 'caja chica'})`
        } y los vales quedarán liquidados.`}
        confirmLabel="Emitir"
        tone="primary"
        loading={liquidar.isPending}
        onClose={() => setConfirmar(false)}
        onConfirm={() =>
          liquidar.mutate(condiciones, {
            onSuccess: (respuesta) => {
              setConfirmar(false);
              setEmitidos(respuesta.comprobantes.map((item) => item.comprobante));
              onAviso(respuesta.mensaje);
            },
            onError: (fallo) => {
              setConfirmar(false);
              setError(fallo instanceof ApiError ? fallo.message : 'No se pudieron emitir los comprobantes.');
            },
          })
        }
      >
        {precalculo.isError ? (
          <Alert variant="warning" title="Pre-cálculo no disponible">
            Pulsa «Pre procesar» para revisar los comprobantes antes de emitir.
          </Alert>
        ) : null}
      </ConfirmDialog>
    </Dialog>
  );
}
